import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const EventRegistration = ({ event, onRegistrationComplete }) => {
  const { currentUser, userProfile } = useAuth();
  
  // State management
  const [selectedTicket, setSelectedTicket] = useState('');
  const [ticketPrice, setTicketPrice] = useState(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Legal agreement checkboxes
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  // Check if user is actually logged in and is a member
  const isMember = userProfile?.role === 'admin' || (userProfile?.status === 'approved' && userProfile?.membershipTier);

  // Handle ticket selection
  const handleTicketSelect = (ticketId) => {
    setSelectedTicket(ticketId);
    
    // Simple pricing logic
    let price = 0;
    if (ticketId === 'member') {
      price = event.ticketTypes?.find(t => t.id === 'member')?.price || 25;
    } else if (ticketId === 'non-member') {
      price = event.ticketTypes?.find(t => t.id === 'non-member')?.price || 50;
    }
    
    setTicketPrice(price);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTicket) {
      alert('Please select a ticket type');
      return;
    }

    // Validate legal agreements
    if (!agreedToTerms) {
      alert('Please agree to the Terms and Conditions to continue');
      return;
    }

    if (!agreedToPrivacy) {
      alert('Please agree to the Privacy Policy to continue');
      return;
    }

    if (ticketPrice > 0) {
      // Handle paid registration
      await handleStripePayment();
    } else {
      // Handle free registration
      await handlePaymentSuccess();
    }
  };

  // Handle Stripe payment
  const handleStripePayment = async () => {
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      const eventIdString = String(event.id);
      const finalPrice = ticketPrice || 0;

      // Map frontend ticket IDs to backend registration types
      const registrationTypeMap = {
        'member': 'member',
        'non-member': 'nonMember',
        'sponsor': 'sponsor'
      };

      // Create Stripe checkout session for event registration
      const requestBody = {
        eventId: eventIdString,
        userId: currentUser?.uid || 'anonymous',
        registrationType: registrationTypeMap[selectedTicket] || selectedTicket,
        eventTitle: event.title,
        eventPrice: finalPrice,
        successUrl: `${window.location.origin}/events/${event.id}?success=true`,
        cancelUrl: `${window.location.origin}/events/${event.id}?canceled=true`
      };

      const response = await fetch('https://us-central1-the-apn.cloudfunctions.net/createEventCheckoutSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment processing failed');
      }

      const { url } = await response.json();
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No payment URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      // Use specific error message from backend if available
      let errorMessage = 'Payment failed. Please try again.';
      if (error.message && error.message !== 'Failed to fetch') {
        errorMessage = error.message;
      }
      
      setPaymentError(errorMessage);
      setPaymentLoading(false);
    }
  };

  // Handle successful payment/registration
  const handlePaymentSuccess = async () => {
    setRegistrationComplete(true);
    setPaymentLoading(false);
    if (onRegistrationComplete) {
      onRegistrationComplete({
        eventId: event.id,
        ticketType: selectedTicket,
        finalPrice: ticketPrice
      });
    }
  };

  if (registrationComplete) {
    return (
      <div className="text-center py-8">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">Registration Successful!</h3>
          <p className="text-green-700">
            Thank you for registering for {event.title}. You will receive a confirmation email shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Registration</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Select Registration Type</h3>
            <div className="space-y-3">
              {isMember && (
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="ticket"
                    value="member"
                    checked={selectedTicket === 'member'}
                    onChange={() => handleTicketSelect('member')}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <span className="font-medium">Member Registration</span>
                    <span className="ml-2 text-green-600">${event.ticketTypes?.find(t => t.id === 'member')?.price || 25}</span>
                  </div>
                </label>
              )}
              
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="ticket"
                  value="non-member"
                  checked={selectedTicket === 'non-member'}
                  onChange={() => handleTicketSelect('non-member')}
                  className="mr-3"
                />
                <div className="flex-1">
                  <span className="font-medium">Non-Member Registration</span>
                  <span className="ml-2 text-gray-600">${event.ticketTypes?.find(t => t.id === 'non-member')?.price || 50}</span>
                </div>
              </label>
            </div>
          </div>

          {selectedTicket && ticketPrice !== null && (
            <div className="border-t pt-6">
              {/* Legal Agreements Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Legal Agreements</h4>
                <div className="space-y-3">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I agree to the{' '}
                      <Link
                        to="/legal/terms-and-conditions"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Terms and Conditions
                      </Link>
                      {' '}*
                    </span>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={agreedToPrivacy}
                      onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                      className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I agree to the{' '}
                      <Link
                        to="/legal/privacy-policy"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Privacy Policy
                      </Link>
                      {' '}*
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  * Required for event registration and participation
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total:</span>
                  <span className="text-xl font-bold">${ticketPrice.toFixed(2)}</span>
                </div>
              </div>
              
              {ticketPrice > 0 ? (
                <button
                  type="submit"
                  disabled={paymentLoading || !agreedToTerms || !agreedToPrivacy}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {paymentLoading ? 'Processing...' : `Pay $${ticketPrice.toFixed(2)}`}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!agreedToTerms || !agreedToPrivacy}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Complete Free Registration
                </button>
              )}

              {(!agreedToTerms || !agreedToPrivacy) && (
                <p className="text-sm text-red-600 text-center mt-2">
                  Please agree to both the Terms and Conditions and Privacy Policy to continue
                </p>
              )}
              
              {paymentError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
                  {paymentError}
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EventRegistration;
